import { useState, useMemo, useCallback } from 'react'
import { BarChart3 } from 'lucide-react'
import { usePollingSnapshots } from '@/hooks/usePollingSnapshots'
import type { PollingSnapshot } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PollingSummaryCard } from './PollingSummaryCard'
import { PollingDetailView } from './PollingDetailView'

interface PollingPanelProps {
  partyId: string
  currentTick: number
  className?: string
}

/**
 * Main panel showing a list of polling snapshots for the party.
 * Displays on the party dashboard with click-to-expand details.
 */
export function PollingPanel({
  partyId,
  currentTick,
  className,
}: PollingPanelProps) {
  const { polls, isLoading, error } = usePollingSnapshots(partyId, 10)
  const [selectedPollId, setSelectedPollId] = useState<string | null>(null)

  // Find the selected poll and its previous poll for trend comparison
  const { selectedPoll, previousPoll } = useMemo(() => {
    if (!selectedPollId) return { selectedPoll: null, previousPoll: null }

    const selected = polls.find((p) => p.id === selectedPollId) || null
    if (!selected) return { selectedPoll: null, previousPoll: null }

    // Find the previous poll of the same scope for comparison
    const previous = findPreviousPoll(selected, polls)

    return { selectedPoll: selected, previousPoll: previous }
  }, [selectedPollId, polls])

  const handlePollClick = useCallback((poll: PollingSnapshot) => {
    setSelectedPollId(poll.id)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedPollId(null)
  }, [])

  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Polling</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            <div className="h-24 bg-muted rounded" />
            <div className="h-24 bg-muted rounded" />
            <div className="h-24 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Polling</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-destructive">
            <p className="text-sm">Failed to load polling data</p>
            <p className="text-xs text-muted-foreground mt-1">
              {error.message}
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Empty state
  if (polls.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Polling</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-6 text-center space-y-3">
            <div className="rounded-full bg-muted p-3">
              <BarChart3 className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">No polls yet</p>
              <p className="text-sm text-muted-foreground">
                Queue a Poll action to gather intelligence
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <span>📊</span> Polling
            </CardTitle>
            {polls.length > 3 && (
              <span className="text-xs text-muted-foreground">
                Showing {Math.min(polls.length, 10)} of {polls.length}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {polls.map((poll) => (
            <PollingSummaryCard
              key={poll.id}
              poll={poll}
              playerPartyId={partyId}
              currentTick={currentTick}
              onClick={() => handlePollClick(poll)}
            />
          ))}

          {polls.length >= 10 && (
            <div className="text-center pt-2">
              <p className="text-xs text-muted-foreground">
                Showing most recent 10 polls
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail modal */}
      <PollingDetailView
        poll={selectedPoll}
        playerPartyId={partyId}
        previousPoll={previousPoll}
        isOpen={selectedPollId !== null}
        onClose={handleCloseDetail}
      />
    </>
  )
}

/**
 * Find the previous poll of the same scope (type + target) for trend comparison.
 */
function findPreviousPoll(
  currentPoll: PollingSnapshot,
  allPolls: PollingSnapshot[]
): PollingSnapshot | null {
  return (
    allPolls.find(
      (p) =>
        p.id !== currentPoll.id &&
        p.poll_type === currentPoll.poll_type &&
        p.target_id === currentPoll.target_id &&
        p.tick < currentPoll.tick
    ) || null
  )
}
